import { resolveLanguage } from './language';
import type { Language } from './language';
import { text } from './text';
import type { MessageId } from './text';

/**
 * What a client is asked to agree to, and the shape of the record that says
 * they did.
 *
 * ## Why this module exists at all, when nothing is stored
 *
 * `legal-review-at-the-gate` removed the standing legal gate but kept a short
 * **carve-out**: findings whose absence at the moment of first real use creates
 * a record no later edit can repair. Two of the four live in this slice, and the
 * first is this one:
 *
 * > Every consent decision SHALL be recorded with the exact text the client was
 * > shown, the language it was rendered in, the version of the source text,
 * > which consent it was, where in the product it was given, and when. Records
 * > SHALL be append-only, one row per decision, with withdrawal recorded as a
 * > new decision rather than an edit or a deletion.
 *
 * This change adds no table and no migration (`design.md`, Non-Goals), so
 * nothing here reaches a database. What it does is fix the **shape** while the
 * screens that will carry it are being written, because that is the part that
 * cannot be retrofitted: you can reword a sentence later, and you cannot
 * reconstruct what somebody read in 2026.
 *
 * ## The three things the record must not get wrong
 *
 *   1. **The text is stored verbatim, not as a reference.** `consent.ack.text`
 *      contains `{coach}` and `{role}`, and both resolve from the
 *      `coach_instance` row at the moment of rendering. She can change her role
 *      line tomorrow — she is a personal trainer who is also a medical student,
 *      and that line is hers to word — and a record pointing at a template would
 *      then claim she agreed to sentence that did not exist yet. So the record
 *      carries the resolved string.
 *   2. **The language is part of the record.** Castilian is the only supported
 *      language today (`./language.ts`, decided by the owner on 2026-09-14), and
 *      counsel's Spanish pass holds at low confidence that Catalan may bind. A
 *      record that does not name its language is a record that cannot survive
 *      that answer arriving.
 *   3. **A decision is never edited.** Giving, declining, withdrawing and
 *      re-giving are four decisions and four records. Nothing in this module
 *      returns a mutable record or offers a way to change one.
 *
 * ## What is deliberately not here
 *
 * **No date of birth, and no age.** The carve-out's second item is age, and its
 * shape is a coach attestation plus one client tick — `'age'` below — with
 * neither a birth date nor a number of years stored anywhere. There is no field
 * in `ConsentRecord` that could hold one, which is the point.
 */

/**
 * The version of the wording in `src/shared/text/es.ts`.
 *
 * **Move it whenever any `consent.*.text` string changes**, including a typo
 * fix. It is what lets a record from before a rewording still be read as having
 * been given against the earlier words, and it is cheap: one line, in the same
 * commit as the reword.
 *
 * A date rather than a number, because that is how the existing
 * `client.ack_version` and `client.consent_*_version` columns are already
 * seeded (`00002_people.sql`, and the fixtures) and two spellings of the same
 * idea is one too many.
 */
export const CONSENT_TEXT_VERSION = '2026-09-15';

/**
 * The five decisions this product asks a client to make, and there is no sixth.
 *
 * `ack` and `age` are **required**: a client cannot reach a plan, a session or a
 * note without both. The other three are optional, and declining one simply
 * means that feature is not presented (`access-and-consent`).
 */
export const CONSENT_IDS = ['ack', 'age', 'metrics', 'messaging', 'hurt'] as const;

export type ConsentId = (typeof CONSENT_IDS)[number];

/** The two that block, in the order the first-run screen asks them. */
export const REQUIRED_CONSENTS = ['ack', 'age'] as const satisfies readonly ConsentId[];

/** The three that do not, in the order the first-run screen offers them. */
export const OPTIONAL_CONSENTS = [
  'metrics',
  'messaging',
  'hurt',
] as const satisfies readonly ConsentId[];

export type RequiredConsentId = (typeof REQUIRED_CONSENTS)[number];
export type OptionalConsentId = (typeof OPTIONAL_CONSENTS)[number];

export function isOptionalConsent(id: ConsentId): id is OptionalConsentId {
  return (OPTIONAL_CONSENTS as readonly ConsentId[]).includes(id);
}

/** The sentence a client reads, and the short name of the choice in a list. */
const WORDING: Record<ConsentId, { text: MessageId; name: MessageId }> = {
  ack: { text: 'consent.ack.text', name: 'consent.ack.name' },
  age: { text: 'consent.age.text', name: 'consent.age.name' },
  metrics: { text: 'consent.metrics.text', name: 'consent.metrics.name' },
  messaging: { text: 'consent.messaging.text', name: 'consent.messaging.name' },
  hurt: { text: 'consent.hurt.text', name: 'consent.hurt.name' },
};

/**
 * Who the sentence is about. Both come from the instance row, never a literal.
 *
 * It is a `type` with an index signature rather than a plain `interface` so that
 * it is directly usable as `TextParams`: the whole point of this module is that
 * the string on the screen and the string in the record come from one
 * interpolation, and a shape that had to be copied into a second object to be
 * rendered would be two.
 */
export type ConsentParties = {
  coach: string;
  role: string;
} & Readonly<Record<string, string | number>>;

/**
 * The exact sentence to put in front of the client — and, unchanged, into the
 * record.
 *
 * It is one function rather than two so that the string a client reads and the
 * string a record keeps cannot drift apart: there is no second call site that
 * could interpolate differently.
 */
export function consentText(
  id: ConsentId,
  parties: ConsentParties,
  language: Language = resolveLanguage(),
): string {
  return text(WORDING[id].text, parties, language);
}

/** The short name of a choice, for a row on the profile screen and for errors. */
export function consentName(id: ConsentId, language: Language = resolveLanguage()): string {
  return text(WORDING[id].name, undefined, language);
}

/** Where in the product the decision was made. Part of the record. */
export type ConsentSurface = 'first-run' | 'profile';

/**
 * What the client did.
 *
 * Withdrawing is a decision in its own right and re-giving is another one, each
 * with its own record — never an edit of the first. That is the whole of
 * "append-only", stated in the type before it is stated in a table.
 */
export type ConsentDecision = 'given' | 'declined' | 'withdrawn';

/**
 * One decision, as it would be written to the append-only table.
 *
 * Every field is required and every field is a primitive: a record that can be
 * half-filled is a record that will be. `readonly` throughout because the
 * carve-out's "append-only, one row per decision" is a property of the shape
 * before it is a property of any table.
 */
export interface ConsentRecord {
  readonly consent: ConsentId;
  readonly decision: ConsentDecision;
  /** The resolved sentence the client actually read. Never a template. */
  readonly text: string;
  /** The language it was rendered in. */
  readonly language: Language;
  /** The version of the source wording — `CONSENT_TEXT_VERSION` at the time. */
  readonly textVersion: string;
  /** Which screen it was given on. */
  readonly surface: ConsentSurface;
  /** When, as an ISO instant. */
  readonly at: string;
}

/**
 * Build the record for one decision, from the same words that were on screen.
 *
 * It takes the parties rather than a pre-rendered string on purpose: the only
 * way to be sure the record says what the screen said is for both to come from
 * `consentText`, and a caller that passed its own string could pass anything.
 */
export function buildConsentRecord(input: {
  consent: ConsentId;
  decision: ConsentDecision;
  parties: ConsentParties;
  surface: ConsentSurface;
  language?: Language;
  at?: string;
}): ConsentRecord {
  const language = input.language ?? resolveLanguage();
  return {
    consent: input.consent,
    decision: input.decision,
    text: consentText(input.consent, input.parties, language),
    language,
    textVersion: CONSENT_TEXT_VERSION,
    surface: input.surface,
    at: input.at ?? new Date().toISOString(),
  };
}
